import React, { useState, useEffect, useCallback, useRef } from "react";
import { CheckIcon, PlusIcon } from "lucide-react";
import { CpuChipIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import useMqttStore from "../../store/useMqttStore";
import { topicPrefix } from "../../constant";
import { useGetDevicesForLocation } from "../../queries/admin";
import { useValueStore } from "../../store/useValueState";
import { useLocationStore } from "../../store/useLocationStore";
import { useDeviceStore } from "../../store/useDeviceStore";
import { useDashboard } from "../dashboard/hooks/useDashboard";
import AddDeviceToLocationModal from "../../components/modal/AddDeviceToLocationModal";
import { getPreSignedUrlForDownload } from "../../api/admin";

export interface Device {
  id: string;
  name: string;
  location: string;
  imageKey?: string;
}

interface DeviceDisplayProps {
  device: Device;
  isChecked?: boolean;
}

const DeviceDisplay: React.FC<DeviceDisplayProps> = ({ device, isChecked = false }) => {
  const [imageUrl, setImageUrl] = useState<string>();

  useEffect(() => {
    if (!device.imageKey) {
      return;
    }
    const fetchUrl = async () => {
      try {
        const res = await getPreSignedUrlForDownload({ key: device.imageKey! });
        setImageUrl(res.url);
      } catch (error) {
        console.error("Failed to fetch device image:", error);
      }
    };

    fetchUrl();
  }, [device.imageKey]);

  return (
    <div className="flex items-center gap-2 w-full">
      <div
        className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
          isChecked
            ? "bg-primary border-primary"
            : "border-gray-300 dark:border-gray-500"
        }`}
      >
        {isChecked && <CheckIcon className="w-3 h-3 text-white" />}
      </div>
      {imageUrl ? (
        <img
          className="w-6 h-6 rounded object-cover"
          src={imageUrl}
          alt={`${device.name} device`}
        />
      ) : (
        <CpuChipIcon className="w-6 h-6 text-gray-400" />
      )}
      <div className="min-w-0">
        <div className="text-sm truncate">{device.name}</div>
        <div className="text-xs text-gray-500 truncate">{device.location}</div>
      </div>
    </div>
  );
};

const DeviceSelection: React.FC = () => {
  const { selectedLocationId } = useLocationStore();
  const {
    selectedDeviceId,
    selectedDeviceName,
    selectedDeviceIds,
    setSelectedDevice,
    setSelectedDevices,
    toggleDevice,
  } = useDeviceStore();
  const { data: locationDevices, isLoading: devicesLoading } = useGetDevicesForLocation(
    selectedLocationId && selectedLocationId !== "not-available" ? selectedLocationId : null
  );
  const { subscribeTopic } = useMqttStore();
  const { setValue } = useValueStore();
  const { isEditMode } = useDashboard();
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const previousLocationIdRef = useRef<string | null>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = useCallback(
    (device: Device) => {
      toggleDevice(device.id, device.name);
      subscribeTopic(`${topicPrefix}/${device.id.toString()}`);
    },
    [toggleDevice, subscribeTopic]
  );

  const handleSelectAll = useCallback(() => {
    if (!locationDevices || locationDevices.length === 0) return;
    const allSelected = selectedDeviceIds.length === locationDevices.length;
    if (allSelected) {
      // Keep at least one selected — reduce to the first device
      const first = locationDevices[0];
      setSelectedDevice(first.id, first.name);
      subscribeTopic(`${topicPrefix}/${first.id.toString()}`);
      setValue({ deviceName: first.name, deviceId: first.id });
    } else {
      setSelectedDevices(
        locationDevices.map((d: Device) => ({ id: d.id, name: d.name }))
      );
      locationDevices.forEach((device) => {
        subscribeTopic(`${topicPrefix}/${device.id.toString()}`);
      });
    }
  }, [locationDevices, selectedDeviceIds, setSelectedDevice, setSelectedDevices, subscribeTopic, setValue]);

  // Reset device selection whenever location changes or the persisted
  // selection contains devices that don't belong to the current location.
  // NOTE: selectedDeviceIds is read from the store snapshot (not a dependency)
  // to avoid an infinite loop when setSelectedDevice creates a new array ref.
  useEffect(() => {
    if (!selectedLocationId || !locationDevices) return;

    const currentIds = useDeviceStore.getState().selectedDeviceIds;

    const locationChanged =
      previousLocationIdRef.current !== null &&
      previousLocationIdRef.current !== selectedLocationId;
    const validIds = new Set(locationDevices.map((d) => d.id));
    const hasStaleSelection = currentIds.some((id) => !validIds.has(id));
    const noSelection = currentIds.length === 0;

    if (locationChanged || hasStaleSelection || noSelection) {
      if (locationDevices.length === 1) {
        // Only one device — select just that device by default (not "all")
        const only = locationDevices[0];
        setSelectedDevice(only.id, only.name);
        subscribeTopic(`${topicPrefix}/${only.id.toString()}`);
        setValue({ deviceName: only.name, deviceId: only.id });
      } else if (locationDevices.length > 1) {
        // Multiple devices — default to all devices for the location
        setSelectedDevices(
          locationDevices.map((d: Device) => ({ id: d.id, name: d.name }))
        );
        locationDevices.forEach((d: Device) => {
          subscribeTopic(`${topicPrefix}/${d.id.toString()}`);
        });
        const firstDevice = locationDevices[0];
        setValue({ deviceName: firstDevice.name, deviceId: firstDevice.id });
      } else {
        setSelectedDevice("", "");
        setValue({ deviceName: "", deviceId: "" });
      }
    }

    previousLocationIdRef.current = selectedLocationId;
  }, [
    locationDevices,
    selectedLocationId,
    setSelectedDevice,
    setSelectedDevices,
    subscribeTopic,
    setValue,
  ]);

  // Keep useValueStore in sync with the primary (first) selected device
  useEffect(() => {
    if (selectedDeviceId) {
      setValue({ deviceName: selectedDeviceName, deviceId: selectedDeviceId });
    }
  }, [selectedDeviceId, selectedDeviceName, setValue]);

  // Subscribe to MQTT for the primary selected device
  useEffect(() => {
    if (selectedDeviceId) {
      subscribeTopic(`${topicPrefix}/${selectedDeviceId}`);
    }
  }, [selectedDeviceId, subscribeTopic]);

  const getButtonLabel = () => {
    if (selectedDeviceIds.length === 0) return "Select device";
    if (
      locationDevices &&
      locationDevices.length > 1 &&
      selectedDeviceIds.length === locationDevices.length
    ) {
      return "All";
    }
    if (selectedDeviceIds.length === 1) {
      return selectedDeviceName || "";
    }
    return `${selectedDeviceIds.length} selected`;
  };

  return (
    <>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
          Device
        </label>
        {devicesLoading ? (
          <div className="w-64 h-10 px-3 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 text-sm flex items-center">
            Loading devices...
          </div>
        ) : locationDevices && locationDevices.length > 0 ? (
          <div className="flex items-center space-x-1">
            <div className="relative w-64" ref={dropdownRef}>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="h-10 cursor-pointer rounded-lg bg-white dark:bg-gray-800 py-1.5 pl-3 pr-8 text-left text-gray-900 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-none focus:ring-2 focus:ring-primary text-sm sm:leading-6 w-64"
              >
                <span className="block truncate font-medium text-sm">
                  {getButtonLabel()}
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </button>

              {isOpen && (
                <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-[#1F2C33] py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-600 dark:border dark:border-gray-600 focus:outline-none sm:text-sm">
                  {/* Select All row — only relevant when there is more than one device */}
                  {locationDevices.length > 1 && (
                    <li
                      onClick={handleSelectAll}
                      className="relative cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 select-none py-2 px-3 text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 font-medium"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                            selectedDeviceIds.length === locationDevices.length
                              ? "bg-primary border-primary"
                              : "border-gray-300 dark:border-gray-500"
                          }`}
                        >
                          {selectedDeviceIds.length === locationDevices.length && (
                            <CheckIcon className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className="text-sm">All Devices</span>
                      </div>
                    </li>
                  )}
                  {locationDevices.map((device: Device, index: number) => {
                    const isChecked = selectedDeviceIds.includes(device.id);
                    return (
                      <li
                        key={device.id}
                        onClick={() => handleToggle(device)}
                        className={`relative cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 select-none py-2 px-3 text-gray-900 dark:text-white ${
                          isChecked ? "bg-gray-50/50 dark:bg-gray-700/50" : ""
                        } ${
                          index !== locationDevices.length - 1 ? "border-b border-gray-100 dark:border-gray-700" : ""
                        }`}
                      >
                        <DeviceDisplay device={device} isChecked={isChecked} />
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-1">
            <div className="w-64 h-10 px-3 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 text-sm flex items-center">
              {selectedLocationId && selectedLocationId !== "not-available"
                ? "No devices"
                : "Select location"}
            </div>
          </div>
        )}
      </div>

      {/* Add Device Modal */}
      {showAddDeviceModal && (
        <AddDeviceToLocationModal
          isOpen={showAddDeviceModal}
          onClose={() => setShowAddDeviceModal(false)}
        />
      )}
    </>
  );
};

export default DeviceSelection;
